import React, {useEffect, useRef, useState} from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    CircularProgress, Breadcrumbs,
} from '@mui/material';
import {Link, useNavigate, useParams} from 'react-router-dom';
import api from '../../api';
import {NumericFormat} from "react-number-format"; // Adapte a importação conforme seu arquivo api.js
import Loading from '../../components/Loading';
import DidNotLoad from "../../components/DidNotLoad";
import HtmlColorField from '../../components/HtmlColorField';
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import { isHtmlHexColor, normalizeHtmlHex } from '../../helpers/htmlColor';

function EditMaterialPage() {
    const [material, setMaterial] = useState({
        type: '',
        brand: '',
        color: '',
        color_label: '',
        spool_weight: '',
        current_weight: '',
        cost: 0,
    });
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [costPerKilogram, setCostPerKilogram] = useState('');
    const [costPerKilogramFloat, setCostPerKilogramFloat] = useState(undefined);
    const [spoolWeight, setSpoolWeight] = useState('');
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        brand: null,
        type: null,
        color: null,
        color_label: null,
        cost_per_kilogram: null,
        spool_weight: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);

    useEffect(() => {
        api.get(`filaments/${id}`)
            .then(response => {
                const data = response.data.data || {};
                const color = data.color || '';
                const colorLabel = data.color_label
                    || (!isHtmlHexColor(color) ? color : '');
                setMaterial({
                    ...data,
                    color,
                    color_label: colorLabel,
                });
                setSpoolWeight(data.spool_weight ?? '');
                if (typeof data.cost === 'number') {
                    const costPerKgReais = data.cost / 100;
                    setCostPerKilogram(String(costPerKgReais));
                    setCostPerKilogramFloat(costPerKgReais);
                }
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        let hasError = false
        if (material.type === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                type: 'O tipo do material é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (material.brand === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                brand: 'A marca do material é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (material.color === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                color: 'A cor do material é obrigatória.',
                has_errors: true,
            }));
        }

        if (!String(material.color_label || '').trim()) {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                color_label: 'O nome da cor é obrigatório.',
                has_errors: true,
            }));
        }

        if (spoolWeight === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                spool_weight: 'O peso do carretel é obrigatório.',
                has_errors: true,
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        const costPerKgInCents = parseFormattedMoneyToCents(costPerKilogramFloat, costPerKilogram);
        const spoolWeightGrams = parseInt(spoolWeight, 10);
        const updatedMaterial = {
            type: material.type,
            brand: material.brand,
            color: isHtmlHexColor(material.color)
                ? normalizeHtmlHex(material.color)
                : material.color.trim(),
            color_label: String(material.color_label || '').trim(),
            spool_weight: spoolWeightGrams,
            current_weight: material.current_weight ? parseInt(material.current_weight, 10) : spoolWeightGrams,
            cost: costPerKgInCents,
        };

        api.put(`filaments/${id}`, updatedMaterial)
            .then(response => {
                setHasResponse(true);
                console.log('Impressora atualizada com sucesso!', response.data);
                navigate('/admin/materials'); // Redireciona após o sucesso
            })
            .catch(error => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (isLoading) {
        return <Loading/>;
    }

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/materials')} to={'/admin/materials'}>
                    Filamentos
                </Link>
                <Typography color="text.primary">Editando filamento</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Filamento</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tipo de Material (Filamento PLA, Resina, etc)"
                            fullWidth
                            value={material.type}
                            onChange={(e) => setMaterial({ ...material, type: e.target.value })}
                        />
                        {resultErrors.type && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.type}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Marca"
                            fullWidth
                            value={material.brand}
                            onChange={(e) => setMaterial({ ...material, brand: e.target.value })}
                        />
                        {resultErrors.brand && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.brand}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <HtmlColorField
                            label="Cor (hex)"
                            value={material.color}
                            preserveLiteral
                            onChange={(nextColor) => setMaterial({ ...material, color: nextColor })}
                            error={Boolean(resultErrors.color)}
                        />
                        {resultErrors.color && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.color}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome da cor"
                            fullWidth
                            value={material.color_label || ''}
                            onChange={(e) => setMaterial({ ...material, color_label: e.target.value })}
                            placeholder="Ex.: Verde menta, Preto fosco"
                            error={Boolean(resultErrors.color_label)}
                        />
                        {resultErrors.color_label && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.color_label}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Custo por Kilo"
                            fullWidth
                            customInput={TextField}
                            value={costPerKilogram}
                            onValueChange={(values) => {
                                setCostPerKilogram(values.value);
                                setCostPerKilogramFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.cost_per_kilogram && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.cost_per_kilogram}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Peso do carretel (1000g, 500g, 250g)"
                            fullWidth
                            type="number"
                            value={spoolWeight}
                            onChange={(e) => setSpoolWeight(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.spool_weight && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.spool_weight}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Atualizando...' : 'Atualizar Filamento'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default EditMaterialPage;
